import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { DEFAULT_NOTE_TYPES, NOTE_DIRECTIONS } from "@/services/api";
import { CardSeparator } from "@/shared/Components/CardSeparator";
import { AppDrawer } from "@/shared/Components/AppDrawer";
import { NoteEditorMobile } from "@/features/NoteEditor/Components/Layout/mobile";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { mobileNoteEditorSliceActions } from "@/common/NotesTable/model/mobile-note-editor-slice";
import { appRouterPaths } from "@/services/navigation";
import { useLongPress } from "@/shared/hooks/use-long-press";
import { notesManagerTableSlice, notesTableSliceActions } from "@/features/NotesManager/model";
import type { MobileTableProps } from "../types";
import styles from "../styles.module.scss";
import { Header } from "../Header";
import { Body } from "../Body";
import { Footer } from "../Footer";
import { getCardClass, getStatusColor } from "../helpers";

const DELAY = 300;
const MOVEMENT_THRESHOLD = 10;

export const TableCardMobile: React.FC<MobileTableProps> = ({
  content: { flag = NOTE_DIRECTIONS.INCOMING, note = "", avatarSize = "normal", ...content },
  settings,
  status,
  noteType,
  important,
  currentNote,
  widget,
  noteItem,
  upsertNote,
  deleteNote,
  deleteNoteFileById,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const selectedIds = useAppSelector(notesManagerTableSlice.selectors.selectSelectedTableIds);
  const isCurrentSelected = selectedIds.includes(content.id as React.Key);
  const [selectedAction, setSelectedAction] = useState(false);

  const handleClick = () => {
    if (widget && noteItem && content.person) {
      dispatch(mobileNoteEditorSliceActions.setNote(noteItem));
      navigate(
        noteType === DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT
          ? appRouterPaths.commitments
          : appRouterPaths.notes,
      );
      dispatch(mobileNoteEditorSliceActions.setEdit(true));
    }
  };

  const handleClose = () => {
    dispatch(mobileNoteEditorSliceActions.setEdit(false));
  };

  const handleLongPress = () => {
    if (!widget && selectedIds) {
      dispatch(
        notesTableSliceActions.setSelectedRowKeys([...selectedIds, content.id as React.Key]),
      );
    }
    setSelectedAction(true);
  };

  const handleClickSelected = () => {
    if (selectedAction) {
      if (isCurrentSelected) {
        dispatch(
          notesTableSliceActions.setSelectedRowKeys(
            selectedIds.filter((item) => item !== (content.id as React.Key)),
          ),
        );
      }
      setSelectedAction(false);
    }
  };

  const onSelectedClickEvent = useMemo(
    () => ({ onClick: handleClickSelected }),
    [isCurrentSelected, selectedIds, content.id],
  );

  const longPressEvent = useLongPress(handleLongPress, DELAY, MOVEMENT_THRESHOLD);

  return (
    <div {...longPressEvent} {...onSelectedClickEvent}>
      <div
        className={classNames(
          styles.root,
          styles.card,
          styles[
            getCardClass({
              dueDate: content.dueDate,
              status,
              direction: flag,
              noteType: noteType,
            })
          ],
          {
            [styles.active]: isCurrentSelected,
          },
        )}
        onClick={handleClick}
      >
        <Header
          id={content.id ?? ""}
          person={content.person}
          date={content.date}
          flag={flag}
          isShowFlag={settings?.isShowFlag}
          isOptions={settings?.isOptions}
          isEdit={settings?.isEdit}
          avatarSize={avatarSize}
          noteType={noteType}
          status={status}
          deleteNote={deleteNote}
          noteItem={noteItem}
          selected={isCurrentSelected}
          statusColor={getStatusColor({ dueDate: content.dueDate, status })}
        />
        <Body
          title={content.cardTitle}
          isDescriptions={settings?.isNote}
          description={note}
          important={important}
        />
        <Footer
          date={content.date}
          dueDate={content.dueDate}
          isShowFooter={settings?.isShowFooter}
          status={status}
        />
      </div>
      <CardSeparator />
      <AppDrawer
        placement="bottom"
        open={settings?.isEdit}
        closable={false}
        height={`${window.innerHeight - 72}px`}
        onClose={handleClose}
        rootClassName={styles.drawer}
        autoFocus
        destroyOnClose
      >
        {currentNote && upsertNote && (
          <NoteEditorMobile
            currentNote={currentNote}
            onSubmit={upsertNote}
            noteType={noteType}
            deleteNoteFileById={deleteNoteFileById}
          />
        )}
      </AppDrawer>
    </div>
  );
};
